import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

export interface EconomicTransferError {
    category: 'rejected' | 'partial' | 'unknown' | 'failed';
    message: string;
    stage?: string;
    upstreamStatus?: number;
    attachmentsCompleted?: number;
    attachmentInvoiceNumber?: number;
    attachmentVoucherNumber?: number;
    attachmentFileSize?: number;
    attachmentAccountingYear?: string;
    responseDetails?: unknown;
}

@Component({
    selector: 'app-economic-transfer-error',
    standalone: true,
    templateUrl: './economic-transfer-error.component.html',
    styles: [`.error-message, .response-json { white-space: pre-wrap; overflow-wrap: anywhere; }`],
})
export class EconomicTransferErrorComponent {
    failure: EconomicTransferError = { category: 'failed', message: 'Overførslen kunne ikke gennemføres.' };
    errorCodes: string[] = [];
    readableMessage = '';
    formattedResponse = '';
    endpoint = '';
    responseStatus?: number;

    @Input() set error(value: EconomicTransferError) {
        this.failure = value;
        this.errorCodes = [];
        this.endpoint = '';
        this.formattedResponse = '';
        this.responseStatus = value.upstreamStatus;
        this.readableMessage = value.message;
        let response = value.responseDetails;
        // Older API versions wrap a JSON response in an exception message.
        // Parse the JSON structure, without matching or interpreting error wording.
        if (!response) {
            const start = value.message.indexOf('{');
            if (start >= 0) {
                try { response = JSON.parse(value.message.slice(start)); } catch { /* Plain text fallback. */ }
            }
        }
        if (!response || typeof response !== 'object' || Array.isArray(response)) return;
        const fields = response as Record<string, unknown>;
        const text = (key: string) => typeof fields[key] === 'string' ? fields[key] as string : '';
        this.readableMessage = text('detail') || text('message') || text('errorMessage') || text('title') || value.message;
        this.endpoint = text('instance');
        if (typeof fields['status'] === 'number') this.responseStatus = fields['status'];
        const validationMessages: string[] = [];
        const collectCodes = (item: unknown, depth = 0) => {
            if (!item || typeof item !== 'object' || depth > 5) return;
            if (Array.isArray(item)) {
                item.slice(0, 11).forEach(entry => collectCodes(entry, depth + 1));
                return;
            }
            const record = item as Record<string, unknown>;
            if (typeof record['errorCode'] === 'string' && !this.errorCodes.includes(record['errorCode'])) {
                this.errorCodes.push(record['errorCode']);
            }
            if (depth > 0) {
                const message = ['message', 'errorMessage', 'detail', 'title']
                    .map(key => record[key])
                    .find(value => typeof value === 'string' && value.trim());
                if (typeof message === 'string') {
                    const property = typeof record['property'] === 'string' ? record['property'] : '';
                    const explanation = property ? `${property}: ${message}` : message;
                    if (!validationMessages.includes(explanation)) validationMessages.push(explanation);
                }
            }
            collectCodes(record['errors'], depth + 1);
        };
        collectCodes(response);
        if (validationMessages.length) this.readableMessage = validationMessages.join('\n\n');
        this.formattedResponse = JSON.stringify(response, null, 2);
    }
    constructor(public activeModal: NgbActiveModal) {}

    get title() {
        if (this.failure.category === 'partial') return 'Overførslen blev ikke afsluttet';
        if (this.failure.category === 'unknown') return 'Overførslen kunne ikke bekræftes';
        return 'Overførslen kunne ikke gennemføres';
    }

    get description() {
        switch (this.failure.category) {
            case 'partial': return 'Kladdeposterne er oprettet i e-conomic, men overførslen blev ikke afsluttet. Kontrollér overførslen i e-conomic, før du forsøger igen.';
            case 'unknown': return 'Vi kunne ikke bekræfte resultatet. Kontrollér i e-conomic, om posterne er oprettet, før du forsøger igen.';
            case 'rejected': return 'e-conomic afviste forespørgslen. Se fejlbeskeden nedenfor.';
            default: return 'Der opstod en fejl under overførslen. Se fejlbeskeden nedenfor.';
        }
    }

    get stageLabel() {
        const labels: Record<string, string> = {
            preparation: 'Forberedelse af overførsel', journal: 'Klargøring af kladde',
            customers: 'Klargøring af kunder', drafts: 'Oprettelse af kladdeposter',
            documents: 'Hentning af kladdeposter og vedhæftning af PDF', completion: 'Registrering af overførsel i IA3',
        };
        return labels[this.failure.stage ?? ''] ?? 'Overførsel til e-conomic';
    }
}
